/**
 * AppUpdateToast — surfaces a "New version available — reload" toast
 * ==========================================================================
 *
 * How it works, end-to-end (iter51):
 *
 * 1. The service worker installs a new version in the background.
 * 2. `public/index.html`'s SW-registration script detects the update
 *    (new SW reached `installed` state AND there was already a
 *    controller — i.e. this is an UPDATE, not first install) and
 *    dispatches a `mlcn:sw-update-ready` window event whose `detail.reg`
 *    is the ServiceWorkerRegistration.
 * 3. This component listens for that event, remembers the registration,
 *    and shows a persistent Sonner toast with a "Reload for latest"
 *    action button.
 * 4. When the caregiver taps "Reload for latest", we `postMessage`
 *    `{type: "SKIP_WAITING"}` to the waiting SW. The SW's message
 *    handler calls `self.skipWaiting()` which transitions it to
 *    `activating` → `activated`.
 * 5. Once the new SW takes control, the browser fires `controllerchange`
 *    on `navigator.serviceWorker`. The listener in index.html handles
 *    that event by calling `window.location.reload()` — the reload
 *    pulls the fresh bundle through the now-active new SW's
 *    network-first rule for /static/js/*.
 *
 * Result: caregivers get the newest version by tapping ONE button.
 * No DevTools, no site-data clearing, no support tickets.
 *
 * Guards:
 *   - Toast only appears when there's an existing controller (updates,
 *     not first installs). Enforced in the index.html emitter.
 *   - Once dismissed, the toast doesn't re-appear until the NEXT
 *     update. But if the caregiver dismisses and later refreshes on
 *     their own, they'll still get the update — because the SW's
 *     `activate` step wipes old caches whenever it does activate.
 */
import { useEffect, useRef } from "react";
import { toast } from "sonner";

export default function AppUpdateToast() {
    // Track whether we've already shown the toast for this session so we
    // don't stack multiples if the update-ready event fires twice (e.g.
    // once from the load-time waiting-SW check + once from `updatefound`).
    const shownRef = useRef(false);

    useEffect(() => {
        const handler = (evt) => {
            if (shownRef.current) return;
            shownRef.current = true;

            const reg = evt?.detail?.reg;
            // Waiting SW might live on reg.waiting OR reg.installing that
            // finished — both are candidates for SKIP_WAITING.
            const target = () =>
                (reg && (reg.waiting || reg.installing))
                || (navigator.serviceWorker && navigator.serviceWorker.controller);

            toast("A new version of Memory Lane is ready.", {
                id: "app-update-toast",
                description: "Tap reload to get the latest — takes a second.",
                duration: Infinity,
                action: {
                    label: "Reload for latest",
                    onClick: () => {
                        const worker = target();
                        try {
                            if (worker && worker.postMessage) {
                                worker.postMessage({ type: "SKIP_WAITING" });
                                // The `controllerchange` listener in
                                // index.html will fire the actual reload
                                // once the new SW takes control. As a
                                // belt-and-braces fallback, force a
                                // reload after 1.5s in case the SW
                                // never fires that event.
                                setTimeout(() => {
                                    try { window.location.reload(); }
                                    catch (_e) { /* very old browser */ }
                                }, 1500);
                            } else {
                                window.location.reload();
                            }
                        } catch (_e) {
                            window.location.reload();
                        }
                    },
                },
                // Cancel dismisses without reloading — the update will
                // still land on any future refresh via the SW's own
                // activate flow, so this isn't a permanent opt-out.
                cancel: {
                    label: "Later",
                    onClick: () => {
                        try { toast.dismiss("app-update-toast"); }
                        catch (_e) { /* no-op */ }
                    },
                },
            });
        };

        window.addEventListener("mlcn:sw-update-ready", handler);
        return () => window.removeEventListener("mlcn:sw-update-ready", handler);
    }, []);

    // Renders nothing — the toast IS the UI.
    return null;
}
